import { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const statuses = [
  ["PRESENT", "Present"],
  ["LATE", "Late"],
  ["HALF_DAY", "Half Day"],
  ["ABSENT", "Absent"],
  ["ON_LEAVE", "On Leave"],
  ["WEEKLY_OFF", "Weekly Off"],
  ["HOLIDAY", "Holiday"],
];
const noTimeStatuses = new Set(["ABSENT", "ON_LEAVE", "WEEKLY_OFF", "HOLIDAY"]);
const blankSession = (checkInTime = "", checkOutTime = "") => ({
  id: crypto.randomUUID(),
  checkInTime,
  checkOutTime,
});
const toIso = (date, time) => new Date(`${date}T${time}:00`).toISOString();
const minutes = (time) => {
  if (!/^\d{2}:\d{2}$/.test(time || "")) return null;
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};
const formatMinutes = (value) =>
  `${Math.floor(value / 60)}h${value % 60 ? ` ${value % 60}m` : ""}`;

function shiftMinutes(employee) {
  const start = minutes(employee?.workSchedule?.shiftStartTime);
  const end = minutes(employee?.workSchedule?.shiftEndTime);
  if (start === null || end === null) return null;
  return Math.max(
    0,
    (end > start ? end - start : end + 1440 - start) -
      Number(employee.workSchedule?.breakDurationMinutes || 0),
  );
}

export default function ManualAttendanceModal({
  open,
  onClose,
  accessToken,
  attendanceDate,
  onSaved,
}) {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    status: "PRESENT",
    sessions: [blankSession("10:00", "19:00")],
    reason: "",
  });
  const needsSessions = !noTimeStatuses.has(form.status);
  const expectedMinutes = useMemo(
    () => shiftMinutes(selectedEmployee),
    [selectedEmployee],
  );
  const totalWorkedMinutes = useMemo(
    () =>
      form.sessions.reduce((total, row) => {
        const start = minutes(row.checkInTime);
        const end = minutes(row.checkOutTime);
        return start === null || end === null
          ? total
          : total + (end > start ? end - start : end + 1440 - start);
      }, 0),
    [form.sessions],
  );

  const validation = useMemo(() => {
    if (!needsSessions) return "";
    if (!form.sessions.length)
      return "Add at least one check-in/check-out session.";
    const parsed = form.sessions.map((row) => ({
      ...row,
      start: minutes(row.checkInTime),
      end: minutes(row.checkOutTime),
    }));
    if (parsed.some((row) => row.start === null || row.end === null))
      return "Every session needs a check-in and check-out time.";
    if (parsed.some((row) => row.end <= row.start))
      return "Every check-out must be later than its check-in.";
    const ordered = [...parsed].sort((a, b) => a.start - b.start);
    if (
      ordered.some(
        (row, index) =>
          ordered[index + 1] && row.end > ordered[index + 1].start,
      )
    )
      return "Sessions cannot overlap.";
    if (
      form.status === "HALF_DAY" &&
      expectedMinutes !== null &&
      totalWorkedMinutes > Math.ceil(expectedMinutes / 2)
    )
      return `Half Day cannot exceed ${formatMinutes(Math.ceil(expectedMinutes / 2))}.`;
    return "";
  }, [
    needsSessions,
    form.sessions,
    form.status,
    expectedMinutes,
    totalWorkedMinutes,
  ]);

  useEffect(() => {
    if (!open || !accessToken) return;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setEmployees(
          await apiRequest(
            `/attendance/admin/employees?search=${encodeURIComponent(search)}`,
            { accessToken },
          ),
        );
      } catch (requestError) {
        setError(requestError.message || "Unable to load employees.");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, accessToken, search]);
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedEmployee(null);
      setError("");
      setForm({
        status: "PRESENT",
        sessions: [blankSession("10:00", "19:00")],
        reason: "",
      });
    }
  }, [open]);
  if (!open) return null;

  const updateSession = (id, field, value) =>
    setForm((current) => ({
      ...current,
      sessions: current.sessions.map((row) =>
        row.id === id ? { ...row, [field]: value } : row,
      ),
    }));
  const selectEmployee = (employee) => {
    setSelectedEmployee(employee);
    const start = employee.workSchedule?.shiftStartTime || "10:00";
    const end = employee.workSchedule?.shiftEndTime || "19:00";
    setForm((current) => ({
      ...current,
      sessions: [blankSession(start, end)],
    }));
  };
  const save = async (event) => {
    event.preventDefault();
    if (!selectedEmployee) return setError("Select an employee.");
    if (selectedEmployee.joiningDate?.slice(0, 10) > attendanceDate)
      return setError(
        "Attendance cannot be added before the employee joining date.",
      );
    if (validation) return setError(validation);
    if (!form.reason.trim())
      return setError("A reason is required for manual attendance.");
    try {
      setSaving(true);
      setError("");
      await apiRequest("/attendance/manual", {
        method: "POST",
        accessToken,
        body: {
          employeeId: selectedEmployee.id,
          attendanceDate,
          status: form.status,
          ...(needsSessions
            ? {
                sessions: form.sessions.map((row) => ({
                  checkInAt: toIso(attendanceDate, row.checkInTime),
                  checkOutAt: toIso(attendanceDate, row.checkOutTime),
                })),
              }
            : {}),
          reason: form.reason.trim(),
        },
      });
      onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Mark Manual Attendance
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Add every work interval. Gaps are excluded from worked time and
              payroll.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-100 p-3"
          >
            <FiX />
          </button>
        </div>
        <form onSubmit={save}>
          <div className="max-h-[72vh] space-y-6 overflow-y-auto p-8">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div>
              <label className="mb-2 block text-sm font-medium">
                Search employee
              </label>
              <div className="relative">
                <FiSearch className="absolute left-4 top-3.5 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="w-full rounded-xl border px-10 py-3"
                  placeholder="Name or employee code"
                />
              </div>
              <div className="mt-3 max-h-40 overflow-auto rounded-xl border">
                {loading ? (
                  <p className="p-3 text-sm text-gray-500">Searching…</p>
                ) : (
                  employees.map((employee) => (
                    <button
                      type="button"
                      key={employee.id}
                      onClick={() => selectEmployee(employee)}
                      className={`flex w-full items-center gap-3 border-b p-3 text-left last:border-0 ${selectedEmployee?.id === employee.id ? "bg-primary-50" : "hover:bg-gray-50"}`}
                    >
                      <FiUser />
                      <span>
                        <b>{employee.fullName}</b>
                        <small className="ml-2 text-gray-500">
                          {employee.employeeCode}
                        </small>
                      </span>
                      {selectedEmployee?.id === employee.id && (
                        <FiCheckCircle className="ml-auto text-green-600" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
            {selectedEmployee && (
              <div className="rounded-xl bg-primary-50 p-4 text-sm text-primary-900">
                <FiClock className="mr-2 inline" />
                {selectedEmployee.workSchedule?.shiftStartTime || "—"} –{" "}
                {selectedEmployee.workSchedule?.shiftEndTime || "—"}
                {expectedMinutes !== null
                  ? ` · expected work ${formatMinutes(expectedMinutes)}`
                  : ""}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium">
                Date
                <input
                  type="date"
                  value={attendanceDate}
                  disabled
                  className="mt-2 w-full rounded-xl border bg-gray-50 p-3"
                />
              </label>
              <label className="text-sm font-medium">
                Status
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      status: event.target.value,
                      sessions: noTimeStatuses.has(event.target.value)
                        ? []
                        : current.sessions.length
                          ? current.sessions
                          : [blankSession()],
                    }))
                  }
                  className="mt-2 w-full rounded-xl border p-3"
                >
                  {statuses.map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {needsSessions ? (
              <section className="rounded-2xl border border-gray-200 p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Work sessions</h3>
                    <p className="text-xs text-gray-500">
                      Each row is one check-in → check-out interval.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        sessions: [...current.sessions, blankSession()],
                      }))
                    }
                    className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-3 py-2 text-sm font-medium text-white"
                  >
                    <FiPlus /> Add session
                  </button>
                </div>
                <div className="space-y-3">
                  {form.sessions.map((row, index) => (
                    <div
                      key={row.id}
                      className="grid items-end gap-3 rounded-xl bg-gray-50 p-3 md:grid-cols-[1fr_1fr_auto]"
                    >
                      <label className="text-xs font-medium text-gray-600">
                        Session {index + 1} — check in
                        <input
                          type="time"
                          value={row.checkInTime}
                          onChange={(event) =>
                            updateSession(
                              row.id,
                              "checkInTime",
                              event.target.value,
                            )
                          }
                          className="mt-1 w-full rounded-lg border bg-white p-2.5"
                        />
                      </label>
                      <label className="text-xs font-medium text-gray-600">
                        Check out
                        <input
                          type="time"
                          value={row.checkOutTime}
                          onChange={(event) =>
                            updateSession(
                              row.id,
                              "checkOutTime",
                              event.target.value,
                            )
                          }
                          className="mt-1 w-full rounded-lg border bg-white p-2.5"
                        />
                      </label>
                      <button
                        type="button"
                        disabled={form.sessions.length === 1}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            sessions: current.sessions.filter(
                              (item) => item.id !== row.id,
                            ),
                          }))
                        }
                        className="rounded-lg p-3 text-red-600 disabled:opacity-30"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-xl bg-gray-100 p-3 text-sm">
                  Total worked: <b>{formatMinutes(totalWorkedMinutes)}</b>
                  {expectedMinutes !== null
                    ? ` · expected ${formatMinutes(expectedMinutes)}`
                    : ""}
                </div>
              </section>
            ) : (
              <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-800">
                This status cannot have work sessions.
              </div>
            )}
            {validation && (
              <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <FiAlertCircle />
                {validation}
              </div>
            )}
            <label className="block text-sm font-medium">
              Reason *
              <textarea
                value={form.reason}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reason: event.target.value,
                  }))
                }
                rows="3"
                className="mt-2 w-full rounded-xl border p-3"
              />
            </label>
          </div>
          <div className="flex justify-end gap-3 border-t p-5">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-5 py-3"
            >
              Cancel
            </button>
            <button
              disabled={saving || !!validation}
              className="rounded-xl bg-primary-800 px-5 py-3 font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save attendance"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
